import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import {useSelector} from 'react-redux';

import {getGroupByDocument} from 'app/presence/utils';
import {useUsers} from 'app/user/api';

import type {
  ConsumableSubscribedUserData,
  StoredMemberSubscriptionData,
  ValidContextType,
} from 'app/presence/types';
import type {ContentType, UserSerializedType} from 'sweet-types';
type Props = {
  contentType: ContentType;
  objectId: string | number;
  contextFilter?: (context?: ValidContextType) => boolean;
  children: (
    arg0: Record<string, Array<ConsumableSubscribedUserData>>
  ) => JSX.Element;
};

export const PresenceConsumer = ({
  children,
  objectId,
  contentType,
  contextFilter = () => true,
}: Props) => {
  const channelName = getGroupByDocument(contentType, objectId);

  const subscriptionMembers: Array<StoredMemberSubscriptionData> = useSelector(
    (state) => state.presence.getIn(['subscriptions', channelName, 'members'])
  );

  /* sort members by joinedAt value.
      Its required to keep the members order consistent.
      Users subscribed for longer times should be the first ones */
  const orderedMembers = sortBy(subscriptionMembers || {}, [
    ({groupData}) => groupData?.joinedAt,
  ]);

  /* Remove all not required data like timeoutIds */
  const cleanedOrderedMembers = orderedMembers
    .filter(({groupData}) => groupData != null)
    .map(({groupData}) => ({
      user_id: groupData!.user_id,
      context: groupData!.context,
      joinedAt: groupData!.joinedAt,
      additionalData: groupData!.additionalData,
    }));

  /* apply the contextFilter func to get only the members which are
      subscribed to the context we are interested to */
  const contextFilteredCleanedOrderedMembers = cleanedOrderedMembers.filter(
    (member) => contextFilter(member.context)
  );

  /* remove duplicated user and contexts
      it happens when same user open the same page/context in more than one tab
      here we'll the keep the older one and discard the most recent one(s) */
  const orderedCleanedMembersWithUniqueUserAndContext = uniqBy(
    contextFilteredCleanedOrderedMembers,
    ({user_id, context, additionalData}) => {
      if (additionalData == null) {
        return `${user_id}:${context}`;
      } else {
        return `${user_id}:${context}:${additionalData.contentType}.${additionalData.objectId}`;
      }
    }
  );

  const usersToQueryFor = orderedCleanedMembersWithUniqueUserAndContext.map(
    (member) => member.user_id
  );

  const {data: users} = useUsers({id: usersToQueryFor.join(',')});

  const usersMap: Record<number, UserSerializedType> = {};

  users?.results.forEach((user) => {
    usersMap[user.id] = user;
  });

  const userAndContext = orderedCleanedMembersWithUniqueUserAndContext
    .map(
      /* get users data from redux store */
      ({user_id, context, joinedAt, additionalData}) => {
        return {
          context,
          joinedAt,
          additionalData,
          user: usersMap[user_id],
        };
      }
    )
    .filter(
      /* filter out the users which we don't have data yet */
      ({user}) => user != null
    );

  /* re-sort the array using the user oldest context joinedAt
        so the sort is consistent even if we users have multiple contexts */
  const sortedUserAndContext = sortBy(userAndContext, [
    ({joinedAt}) => joinedAt,
  ]);

  const presentMembers = groupBy(sortedUserAndContext, ({context}) => context);

  return children(presentMembers);
};
